import { CalendarCheck, ShieldCheck, UserPen } from "lucide-react";

import { getAuthor } from "@/lib/authors";

function PersonCard({
  slug,
  label,
  icon: Icon,
  note,
}: {
  slug: string;
  label: string;
  icon: typeof UserPen;
  note: string;
}) {
  const person = getAuthor(slug);
  if (!person) return null;
  return (
    <div className="flex gap-4 rounded-xl border border-border bg-card p-5">
      <img
        src={person.photo}
        alt={person.name}
        loading="lazy"
        width={56}
        height={56}
        decoding="async"
        className="h-14 w-14 shrink-0 rounded-full object-cover"
      />
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5 text-success" aria-hidden /> {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-foreground">{person.name}</p>
        <p className="text-xs text-muted-foreground">{person.role} · actief sinds {person.since}</p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{person.bio}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Rol op deze pagina:</span> {note}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">Expertise: {person.expertise.join(" · ")}</p>
      </div>
    </div>
  );
}

/**
 * Verantwoordingsblok: wie schreef de pagina, wie controleerde de feiten en
 * wie leverde de meetdata. Vervangt de anonieme "onze redactie"-formulering.
 */
export function EditorialTeam({
  authorSlug,
  reviewerSlug,
  dataSlug = "sander-overmars",
  updatedAt,
  methodName,
}: {
  authorSlug: string;
  reviewerSlug: string;
  dataSlug?: string;
  updatedAt: string;
  methodName: string;
}) {
  return (
    <section aria-labelledby="redactie" className="scroll-mt-28">
      <h2 id="redactie" className="text-xl font-semibold text-foreground">
        Wie maakte deze {methodName}-pagina?
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Elke betaalmethodepagina heeft een vaste redacteur, een onafhankelijke factchecker en een
        data-analist die de meetreeks aanlevert. Commerciële afspraken hebben geen invloed op de
        volgorde van de casinolijst of op de gemeten waarden.
      </p>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <PersonCard
          slug={authorSlug}
          label="Auteur"
          icon={UserPen}
          note={`Schreef de analyse en voerde de kassatests met ${methodName} uit.`}
        />
        <PersonCard
          slug={reviewerSlug}
          label="Factchecker"
          icon={ShieldCheck}
          note="Controleerde elk cijfer tegen de primaire bron en tekende de publicatie af."
        />
        <PersonCard
          slug={dataSlug}
          label="Data-analist"
          icon={CalendarCheck}
          note="Verwerkte de stopwatchmetingen en berekende de mediane doorlooptijden."
        />
      </div>
      <p className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-secondary/40 p-4 text-xs text-muted-foreground">
        <CalendarCheck className="h-4 w-4 shrink-0 text-success" aria-hidden />
        Laatste redactionele controle: {updatedAt}. Wij herzien deze pagina minimaal elk kwartaal en
        direct na elke wijziging in de kassa van een aanbieder. Fout gevonden? Meld het bij de redactie,
        wij passen het aan en noteren de wijziging.
      </p>
    </section>
  );
}
